import { getAuthToken } from '../../services/auth/tokenStorage';
import React, { useEffect, useState, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  ScrollView,
  TouchableOpacity,
  Image,
} from 'react-native';
import { ms, s, vs } from '../../utils/responsive';
import { BottomNavBar } from '../../components/BottomNavBar';
import { useNavigation, useFocusEffect } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { API_URL } from '../../config';
import { ChecklistCardScroll } from '../../components/ChecklistCardScroll';
import { TaskItem } from '../../components/TaskItem';
import { RootStackParamList } from '../../types/navigation';
import { PASSWORDS_TASKS_CONFIG, PERSONAL_TASKS_CONFIG, MEDICAL_TASKS_CONFIG, CONTACTS_TASKS_CONFIG, EXPENSES_TASKS_CONFIG, FINANCE_TASKS_CONFIG, PROPERTY_TASKS_CONFIG, FINAL_WISHES_TASKS_CONFIG, HEALTHCARE_DIRECTIVES_TASKS_CONFIG, ESTATE_PLANNING_TASKS_CONFIG, CARE_PREFERENCES_TASKS_CONFIG, LOYALTY_POINTS_TASKS_CONFIG } from '../../constants/taskIds';
import { vaultService } from '../../services/vaultService';
import { contactService } from '../../services/contactService';
import { expenseService } from '../../services/expenseService';
import { loyaltyPointService } from '../../services/loyaltyPointService';
import { documentStorageService } from '../../services/documentStorageService';
import { useUserSelection } from '../../contexts/UserSelectionContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { UpgradeBanner } from '../../components/UpgradeBanner';

import { createLogger } from '../../utils/logger';
const logger = createLogger('LoyaltyPointsChecklist');

type NavigationProp = NativeStackNavigationProp<RootStackParamList>;

interface Task {
  id: string;
  title: string;
}

interface Category {
  id: string;
  name: string;
  icon: any;
  count: number;
  totalTasks: number;
}

// Use the new loyalty points tasks configuration
const LOYALTY_POINTS_TASKS = LOYALTY_POINTS_TASKS_CONFIG;

const categories: Category[] = [
  { id: 'all', name: 'All Tasks', icon: require('../../assets/icons/all-tasks.png'), count: 0, totalTasks: PERSONAL_TASKS_CONFIG.length + MEDICAL_TASKS_CONFIG.length + FINANCE_TASKS_CONFIG.length + PROPERTY_TASKS_CONFIG.length + FINAL_WISHES_TASKS_CONFIG.length + HEALTHCARE_DIRECTIVES_TASKS_CONFIG.length + ESTATE_PLANNING_TASKS_CONFIG.length + CARE_PREFERENCES_TASKS_CONFIG.length + PASSWORDS_TASKS_CONFIG.length + CONTACTS_TASKS_CONFIG.length + EXPENSES_TASKS_CONFIG.length + LOYALTY_POINTS_TASKS_CONFIG.length + 8 },
  { id: 'personal', name: 'Personal', icon: require('../../assets/icons/personal.png'), count: 0, totalTasks: PERSONAL_TASKS_CONFIG.length },
  { id: 'medical', name: 'Medical', icon: require('../../assets/icons/medical.png'), count: 0, totalTasks: MEDICAL_TASKS_CONFIG.length },
  { id: 'finance', name: 'Finance', icon: require('../../assets/icons/financial.png'), count: 0, totalTasks: FINANCE_TASKS_CONFIG.length },
  { id: 'property', name: 'Property', icon: require('../../assets/icons/property.png'), count: 0, totalTasks: PROPERTY_TASKS_CONFIG.length },
  { id: 'finalWishes', name: 'Final Wishes', icon: require('../../assets/icons/final-wishes.png'), count: 0, totalTasks: FINAL_WISHES_TASKS_CONFIG.length },
  { id: 'healthcareDirectives', name: 'Healthcare Directives', icon: require('../../assets/icons/healthcare.png'), count: 0, totalTasks: HEALTHCARE_DIRECTIVES_TASKS_CONFIG.length },
  { id: 'estatePlanning', name: 'Estate Planning', icon: require('../../assets/icons/estate-planning.png'), count: 0, totalTasks: ESTATE_PLANNING_TASKS_CONFIG.length },
  { id: 'carePreferences', name: 'Care Preferences', icon: require('../../assets/icons/final-care.png'), count: 0, totalTasks: CARE_PREFERENCES_TASKS_CONFIG.length },
  { id: 'passwords', name: 'Passwords', icon: require('../../assets/icons/password.png'), count: 0, totalTasks: PASSWORDS_TASKS_CONFIG.length },
  { id: 'contacts', name: 'Contacts', icon: require('../../assets/icons/all-contacts.png'), count: 0, totalTasks: CONTACTS_TASKS_CONFIG.length },
  { id: 'expenses', name: 'Expenses', icon: require('../../assets/icons/expenses.png'), count: 0, totalTasks: EXPENSES_TASKS_CONFIG.length },
  { id: 'documents', name: 'Documents', icon: require('../../assets/icons/folder.png'), count: 0, totalTasks: 8 },
  { id: 'loyaltyPoints', name: 'Loyalty Points', icon: require('../../assets/icons/loyalty.png'), count: 0, totalTasks: LOYALTY_POINTS_TASKS_CONFIG.length },
];

export const LoyaltyPointsChecklistScreen: React.FC = () => {
  const navigation = useNavigation<NavigationProp>();
  const { isViewingOthersData, isDeathVerifiedForSelectedUser, selectedUserId, availablePrimaryUsers } = useUserSelection();
  const { canViewOwnData } = useSubscription();
  const [selectedCategory, setSelectedCategory] = useState('loyaltyPoints');
  const [completedTaskIds, setCompletedTaskIds] = useState<string[]>([]);
  const [categoryProgress, setCategoryProgress] = useState<Record<string, number>>({});
  const [completedPasswordTasks, setCompletedPasswordTasks] = useState<string[]>([]);
  const [completedContactTasks, setCompletedContactTasks] = useState<string[]>([]);
  const [completedExpenseTasks, setCompletedExpenseTasks] = useState<string[]>([]);
  const [completedLoyaltyPointTasks, setCompletedLoyaltyPointTasks] = useState<string[]>([]);
  const [completedPersonalTasks, setCompletedPersonalTasks] = useState<string[]>([]);
  const [completedMedicalTasks, setCompletedMedicalTasks] = useState<string[]>([]);
  const [completedFinanceTasks, setCompletedFinanceTasks] = useState<string[]>([]);
  const [completedPropertyTasks, setCompletedPropertyTasks] = useState<string[]>([]);
  const [completedFinalWishesTasks, setCompletedFinalWishesTasks] = useState<string[]>([]);
  const [completedHealthcareDirectivesTasks, setCompletedHealthcareDirectivesTasks] = useState<string[]>([]);
  const [completedEstatePlanningTasks, setCompletedEstatePlanningTasks] = useState<string[]>([]);
  const [completedCarePreferencesTasks, setCompletedCarePreferencesTasks] = useState<string[]>([]);
  const [completedDocumentTasks, setCompletedDocumentTasks] = useState<string[]>([]);

  // Get the name of the user being viewed
  const viewingUserName = isViewingOthersData
    ? availablePrimaryUsers.find(u => u.id === selectedUserId)?.name || 'User'
    : null;

  // Loyalty points require death verification when viewing another user's data
  const requiresDeathVerification = isViewingOthersData && !isDeathVerifiedForSelectedUser;

  // Subscription access control
  const isTaskDisabled = !canViewOwnData && !isViewingOthersData;

  // Fetch loyalty point completion data - extracted for reuse
  const fetchLoyaltyPointCompletion = useCallback(async () => {
    try {
      // Add timeout to prevent hanging
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error('Loyalty point fetch timeout')), 3000);
      });

      // K5: the owner's list when viewing another person after a verified death.
      const fetchPromise = requiresDeathVerification
        ? Promise.resolve([])
        : loyaltyPointService.getAllLoyaltyPoints(isViewingOthersData ? selectedUserId : undefined, { quiet: true });
      const allLoyaltyPoints = await Promise.race([fetchPromise, timeoutPromise]);
      const completedLoyaltyPointTasksList: string[] = [];

      LOYALTY_POINTS_TASKS_CONFIG.forEach(task => {
        if (task.category) {
          const categoryLoyaltyPoints = allLoyaltyPoints.filter(lp => lp.category?.toLowerCase() === task.category?.toLowerCase());
          if (categoryLoyaltyPoints.length > 0) {
            completedLoyaltyPointTasksList.push(task.id.toString());
          }
        }
      });

      setCompletedLoyaltyPointTasks(completedLoyaltyPointTasksList);
      logger.debug('Loyalty point completion refreshed:', completedLoyaltyPointTasksList.length, 'of', LOYALTY_POINTS_TASKS_CONFIG.length, 'tasks completed');
    } catch (error) {
      logger.error('Error fetching completed loyalty point tasks (using fallback):', error);
      setCompletedLoyaltyPointTasks([]);
    }
  }, []);

  // Refresh loyalty point completion when screen gains focus
  useFocusEffect(
    useCallback(() => {
      logger.debug('LoyaltyPointsChecklistScreen focused - refreshing loyalty point completion data');
      fetchLoyaltyPointCompletion();
    }, [fetchLoyaltyPointCompletion])
  );

  useEffect(() => {
    const fetchCompletedTasks = async () => {
      try {
        const token = await getAuthToken();
        if (!token) {
          setCompletedTaskIds([]);
          return;
        }
        const response = await fetch(`${API_URL}/completed-tasks`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (!response.ok) {
          setCompletedTaskIds([]);
          return;
        }
        const data = await response.json();
        setCompletedTaskIds(data.completedTasks || []);
      } catch (error) {
        setCompletedTaskIds([]);
      }
    };
    const fetchCategoryProgress = async () => {
      try {
        const token = await getAuthToken();
        if (!token) return;
        const response = await fetch(`${API_URL}/category-progress`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (!response.ok) return;
        const data = await response.json();
        setCategoryProgress(data.progress || {});
      } catch (error) {
        // ignore
      }
    };

    const fetchPasswordCompletion = async () => {
      try {
        // For password tasks, determine completion based on actual password data
        const allPasswords = await vaultService.getAllPasswords();
        const completedPasswordTasks: string[] = [];
        
        PASSWORDS_TASKS_CONFIG.forEach(task => {
          if (task.category) {
            const categoryPasswords = allPasswords.filter(p => p.category.toLowerCase() === task.category.toLowerCase());
            if (categoryPasswords.length > 0) {
              completedPasswordTasks.push(task.id.toString());
            }
          }
        });
        
        setCompletedPasswordTasks(completedPasswordTasks);
      } catch (error) {
        logger.error('Error fetching completed password tasks:', error);
        setCompletedPasswordTasks([]);
      }
    };

    const fetchContactCompletion = async () => {
      try {
        // For contact tasks, determine completion based on actual contact data
        const allContacts = await contactService.getAllContacts();
        const completedContactTasks: string[] = [];
        
        CONTACTS_TASKS_CONFIG.forEach(task => {
          if (task.category) {
            const categoryContacts = allContacts.filter(c => c.category.toLowerCase() === task.category.toLowerCase());
            if (categoryContacts.length > 0) {
              completedContactTasks.push(task.id.toString());
            }
          }
        });
        
        setCompletedContactTasks(completedContactTasks);
      } catch (error) {
        logger.error('Error fetching completed contact tasks:', error);
        setCompletedContactTasks([]);
      }
    };

    const fetchExpenseCompletion = async () => {
      try {
        // For expense tasks, determine completion based on actual expense data
        const allExpenses = await expenseService.getAllExpenses();
        const completedExpenseTasks: string[] = [];

        EXPENSES_TASKS_CONFIG.forEach(task => {
          if (task.category) {
            const categoryExpenses = allExpenses.filter(e => e.category.toLowerCase() === task.category.toLowerCase());
            if (categoryExpenses.length > 0) {
              completedExpenseTasks.push(task.id.toString());
            }
          }
        });

        setCompletedExpenseTasks(completedExpenseTasks);
      } catch (error) {
        logger.error('Error fetching completed expense tasks:', error);
        setCompletedExpenseTasks([]);
      }
    };

    const fetchDocumentCompletion = async () => {
      try {
        const allDocuments = await documentStorageService.listDocuments();
        const completedDocumentTasksList: string[] = [];

        const DOCUMENT_CATEGORIES = ['personal', 'legal', 'medical', 'financial', 'property', 'insurance', 'digital', 'other'];
        const DOCUMENT_TASK_IDS = ['dm1', 'dm2', 'dm3', 'dm4', 'dm5', 'dm6', 'dm7', 'dm8'];

        DOCUMENT_CATEGORIES.forEach((category, index) => {
          const categoryDocuments = allDocuments.filter(doc => doc.category?.toLowerCase() === category);
          if (categoryDocuments.length > 0) {
            completedDocumentTasksList.push(DOCUMENT_TASK_IDS[index]);
          }
        });

        setCompletedDocumentTasks(completedDocumentTasksList);
      } catch (error) {
        logger.error('Error fetching completed document tasks:', error);
        setCompletedDocumentTasks([]);
      }
    };

    const fetchCompletedTasksFromBackend = async () => {
      try {
        const token = await getAuthToken();
        if (!token) return;

        const res = await fetch(`${API_URL}/api/tasks/user/completed-tasks`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });

        if (res.ok) {
          const data = await res.json();
          const completedTaskIds = (data.completedTaskIds || []).map((id: any) => Number(id));

          const personalIds = new Set(PERSONAL_TASKS_CONFIG.map(t => Number(t.id)));
          const medicalIds = new Set(MEDICAL_TASKS_CONFIG.map(t => Number(t.id)));
          const financeIds = new Set(FINANCE_TASKS_CONFIG.map(t => Number(t.id)));
          const propertyIds = new Set(PROPERTY_TASKS_CONFIG.map(t => Number(t.id)));
          const finalWishesIds = new Set(FINAL_WISHES_TASKS_CONFIG.map(t => Number(t.id)));
          const healthcareDirectivesIds = new Set(HEALTHCARE_DIRECTIVES_TASKS_CONFIG.map(t => Number(t.id)));
          const estatePlanningIds = new Set(ESTATE_PLANNING_TASKS_CONFIG.map(t => Number(t.id)));
          const carePreferencesIds = new Set(CARE_PREFERENCES_TASKS_CONFIG.map(t => Number(t.id)));

          setCompletedPersonalTasks(completedTaskIds.filter((id: number) => personalIds.has(id)).map((id: number) => id.toString()));
          setCompletedMedicalTasks(completedTaskIds.filter((id: number) => medicalIds.has(id)).map((id: number) => id.toString()));
          setCompletedFinanceTasks(completedTaskIds.filter((id: number) => financeIds.has(id)).map((id: number) => id.toString()));
          setCompletedPropertyTasks(completedTaskIds.filter((id: number) => propertyIds.has(id)).map((id: number) => id.toString()));
          setCompletedFinalWishesTasks(completedTaskIds.filter((id: number) => finalWishesIds.has(id)).map((id: number) => id.toString()));
          setCompletedHealthcareDirectivesTasks(completedTaskIds.filter((id: number) => healthcareDirectivesIds.has(id)).map((id: number) => id.toString()));
          setCompletedEstatePlanningTasks(completedTaskIds.filter((id: number) => estatePlanningIds.has(id)).map((id: number) => id.toString()));
          setCompletedCarePreferencesTasks(completedTaskIds.filter((id: number) => carePreferencesIds.has(id)).map((id: number) => id.toString()));
        }
      } catch (error) {
        logger.error('Error fetching completed tasks from backend:', error);
      }
    };

    fetchCompletedTasks();
    fetchCategoryProgress();
    fetchPasswordCompletion();
    fetchContactCompletion();
    fetchExpenseCompletion();
    fetchDocumentCompletion();
    fetchCompletedTasksFromBackend();
    // Note: fetchLoyaltyPointCompletion is called via useFocusEffect
  }, []);

  const handleCategoryPress = (categoryId: string) => {
    setSelectedCategory(categoryId);
    switch (categoryId) {
      case 'all':
        navigation.navigate('Dashboard', { screen: 'AllTasks' });
        break;
      case 'personal':
        navigation.navigate('Dashboard', { screen: 'PersonalChecklist' });
        break;
      case 'medical':
        navigation.navigate('Dashboard', { screen: 'MedicalChecklist' });
        break;
      case 'finance':
        navigation.navigate('Dashboard', { screen: 'FinanceChecklist' });
        break;
      case 'property':
        navigation.navigate('Dashboard', { screen: 'PropertyChecklist' });
        break;
      case 'finalWishes':
        navigation.navigate('Dashboard', { screen: 'FinalWishesChecklist' });
        break;
      case 'healthcareDirectives':
        navigation.navigate('Dashboard', { screen: 'HealthcareDirectivesChecklist' });
        break;
      case 'estatePlanning':
        navigation.navigate('Dashboard', { screen: 'EstatePlanningChecklist' });
        break;
      case 'carePreferences':
        navigation.navigate('Dashboard', { screen: 'CarePreferencesChecklist' });
        break;
      case 'passwords':
        navigation.navigate('Dashboard', { screen: 'OnlineAccountsChecklist' });
        break;
      case 'contacts':
        navigation.navigate('Dashboard', { screen: 'EmergencyContactsChecklist' });
        break;
      case 'expenses':
        navigation.navigate('Dashboard', { screen: 'ExpensesChecklist' });
        break;
      case 'documents':
        navigation.navigate('Dashboard', { screen: 'DocumentManagementChecklist' });
        break;
      case 'loyaltyPoints':
        navigation.navigate('Dashboard', { screen: 'LoyaltyPointsChecklist' });
        break;
    }
  };



  const getCategoryCount = (categoryId: string) => {
    if (categoryId === 'all') {
      return completedPersonalTasks.length + completedMedicalTasks.length + completedFinanceTasks.length +
             completedPropertyTasks.length + completedFinalWishesTasks.length + completedHealthcareDirectivesTasks.length +
             completedEstatePlanningTasks.length + completedCarePreferencesTasks.length +
             completedPasswordTasks.length + completedContactTasks.length + completedExpenseTasks.length +
             completedLoyaltyPointTasks.length + completedDocumentTasks.length;
    } else if (categoryId === 'personal') {
      return completedPersonalTasks.length;
    } else if (categoryId === 'medical') {
      return completedMedicalTasks.length;
    } else if (categoryId === 'finance') {
      return completedFinanceTasks.length;
    } else if (categoryId === 'property') {
      return completedPropertyTasks.length;
    } else if (categoryId === 'finalWishes') {
      return completedFinalWishesTasks.length;
    } else if (categoryId === 'healthcareDirectives') {
      return completedHealthcareDirectivesTasks.length;
    } else if (categoryId === 'estatePlanning') {
      return completedEstatePlanningTasks.length;
    } else if (categoryId === 'carePreferences') {
      return completedCarePreferencesTasks.length;
    } else if (categoryId === 'passwords') {
      return completedPasswordTasks.length;
    } else if (categoryId === 'contacts') {
      return completedContactTasks.length;
    } else if (categoryId === 'expenses') {
      return completedExpenseTasks.length;
    } else if (categoryId === 'documents') {
      return completedDocumentTasks.length;
    } else if (categoryId === 'loyaltyPoints') {
      return completedLoyaltyPointTasks.length;
    } else {
      return 0;
    }
  };

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.mainContent}>
        <View style={styles.header}>
          <TouchableOpacity 
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <Image
              source={require('../../assets/icons/back.png')}
              style={styles.backIcon}
            />
          </TouchableOpacity>
          <Image
            source={require('../../assets/logo.png')}
            style={styles.logo}
            resizeMode="contain"
          />
          <View style={styles.headerPlaceholder} />
        </View>
        <ChecklistCardScroll
          categories={categories.map(cat => ({
            ...cat,
            count: getCategoryCount(cat.id),
            totalTasks: cat.totalTasks
          }))}
          selectedCategory={selectedCategory}
          onCategoryPress={handleCategoryPress}
          navigation={navigation}
        />
        <Text style={styles.title}>Loyalty Points Checklist</Text>

        {/* Death Verification Required Banner for successors */}
        {requiresDeathVerification && (
          <View style={styles.deathVerificationBanner}>
            <Text style={styles.deathVerificationTitle}>Access Restricted</Text>
            <Text style={styles.deathVerificationText}>
              Loyalty points information for {viewingUserName} will only be accessible to Successors after an end of life event has been verified.
            </Text>
            <Text style={styles.deathVerificationSubtext}>
              To gain access, a Successor must submit a death certificate through the app and complete the verification process. If you are a Keyholder, your access is restricted.
            </Text>
          </View>
        )}

        {/* Show content only if not requiring death verification */}
        {!requiresDeathVerification && (
          <>
            {/* Upgrade banner for non-subscribers viewing own data */}
            {!isViewingOthersData && <UpgradeBanner variant="compact" />}

            {/* View All Loyalty Points button - only show if loyalty points exist */}
            {completedLoyaltyPointTasks.length > 0 && (
              <TouchableOpacity style={styles.viewAllButton} onPress={() => navigation.navigate('Loyalty' as never, { screen: 'LoyaltyPointDashboardScreen' } as never)}>
                <Text style={styles.viewAllButtonText}>View All Loyalty Points</Text>
              </TouchableOpacity>
            )}

            <ScrollView style={styles.contentContainer} contentContainerStyle={{ paddingBottom: 200 }}>
              {LOYALTY_POINTS_TASKS.map(task => {
                const isCompleted = completedLoyaltyPointTasks.includes(task.id.toString());
                const buttonText = isCompleted ? 'Manage Loyalty Points' : 'Get Started';
                const buttonColor = isCompleted ? '#FF9F5A' : '#29BFC0';

                const handleTaskPress = () => {
                  // Navigate to loyalty point dashboard with selected category
                  navigation.navigate('Loyalty' as never, {
                    screen: 'LoyaltyPointDashboardScreen',
                    params: { selectedCategory: task.category }
                  } as never);
                };

                return (
                  <TaskItem
                    key={task.id}
                    task={{ id: String(task.id), title: task.title }}
                    onPress={handleTaskPress}
                    isCompleted={isCompleted}
                    onCheckboxPress={() => {}}
                    buttonText={buttonText}
                    buttonColor={buttonColor}
                    isDisabled={isTaskDisabled}
                  />
                );
              })}
            </ScrollView>
          </>
        )}
      </View>
      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F3F6F8',
    paddingTop: ms(25),
  },
  mainContent: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: ms(15),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    top: '10%',
    transform: [{ translateY: -12 }],
    padding: ms(5),
    zIndex: 1,
  },
  backIcon: {
    width: s(30),
    height: s(30),
    tintColor: '#333',
  },
  logo: {
    width: ms(125, 0.3),
    height: ms(125, 0.3),
  },
  headerPlaceholder: {
    width: s(30),
    height: s(30),
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: 'bold',
    color: '#1C2C4F',
    marginLeft: ms(30),
    marginVertical: 0,
    marginBottom: ms(10),
  },
  viewAllButton: {
    backgroundColor: '#29BFC0',
    borderRadius: ms(25),
    marginHorizontal: ms(15),
    marginBottom: ms(18),
    paddingHorizontal: ms(18),
    paddingVertical: ms(12),
    alignSelf: 'stretch',
  },
  viewAllButtonText: {
    color: '#fff',
    fontWeight: 'bold',
    fontSize: ms(15, 0.3),
  },
  contentContainer: {
    flex: 1,
    paddingHorizontal: ms(20),
  },
  deathVerificationBanner: {
    backgroundColor: '#FEF3C7',
    borderWidth: ms(1),
    borderColor: '#F59E0B',
    borderRadius: ms(12),
    padding: ms(20),
    marginHorizontal: ms(20),
    marginVertical: ms(20),
  },
  deathVerificationTitle: {
    fontSize: ms(18, 0.3),
    fontWeight: 'bold',
    color: '#92400E',
    marginBottom: ms(10),
    textAlign: 'center',
  },
  deathVerificationText: {
    fontSize: ms(14, 0.3),
    color: '#92400E',
    textAlign: 'center',
    marginBottom: ms(10),
    lineHeight: ms(20, 0.3),
  },
  deathVerificationSubtext: {
    fontSize: ms(12, 0.3),
    color: '#B45309',
    textAlign: 'center',
    lineHeight: ms(18, 0.3),
  },
}); 